import React, { useState, useEffect, useCallback } from "react";
import { Layout, Layouts, Responsive, WidthProvider } from "react-grid-layout";
import { Integration, Status } from "/app/src/models";
import { DraggableStatus } from "./draggableStatus";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { statusService } from "/app/src/services";
import { handlePromiseError } from "/app/src/helpers/api";
const ResponsiveReactGridLayout = WidthProvider(Responsive);

/**
 * Component for containing the draggable statuses in Order Builder
 */
export default function DraggableStatusArea({
  statuses,
  integration,
}: {
  statuses: Status[];
  integration: Integration;
}) {
  const compactType = "vertical";
  const [isMounted, setIsMounted] = useState(false);
  const [layouts, setLayouts] = useState<Layouts>();

  const cols = { lg: 1, md: 1, sm: 1, xs: 1, xxs: 1 };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const queryClient = useQueryClient();

  const { mutateAsync: updateStatus } = useMutation({
    mutationFn: (values: Status) => {
      return statusService
        .updateSingle(values.id, {
          ...values,
        })
        .then(handlePromiseError);
    },
    onSuccess: (response) => {
      queryClient.setQueryData(
        ["statuses", "custom", integration.id],
        (oldData: { statuses: Status[] }) => {
          return {
            statuses: oldData.statuses.map((status) => {
              if (status.id === response.status.id) {
                return response.status;
              }
              return status;
            }),
          };
        },
      );
    },
  });

  const updateStatusPosition = useCallback(
    (value, position) => {
      updateStatus({
        id: Number(value.i),
        position: position.toString(),
      });
      value.y = position;
    },
    [updateStatus],
  );

  const onLayoutChange = useCallback(
    (layout: Layout[], layouts: Layouts) => {
      if (layout) {
        setLayouts(layouts);

        layout.forEach((item) => {
          const id = parseInt(item.i);
          const status = statuses.find((col) => col.id === id);
          if (!status) return;

          const newPosition = Math.max(0, item.y); // Ensure non-negative position

          // Update position if changed
          const currentPosition = parseInt(status.position ?? "0");
          if (currentPosition !== newPosition) {
            updateStatusPosition(item, newPosition);
          }
        });
      }
    },
    [statuses, updateStatusPosition],
  );
  const children = React.useMemo(() => {
    return statuses.map((status) => {
      const grid = {
        w: 1,
        h: 1,
        x: 0,
        y: statuses.length - 1,
        isResizable: false,
        isDraggable: status.type === "custom",
      };

      if (status?.position) {
        grid["y"] = parseInt(status.position);
      }

      return (
        <div key={status.id} className="reportColumn" data-grid={grid}>
          <DraggableStatus
            key={status.id}
            status={status}
            updateStatus={updateStatus}
          />
        </div>
      );
    });
  }, [statuses, updateStatus]);

  return (
    <div className="layout">
      <ResponsiveReactGridLayout
        className="layout"
        rowHeight={60}
        onLayoutChange={onLayoutChange}
        cols={cols}
        layouts={layouts}
        measureBeforeMount={false}
        useCSSTransforms={isMounted}
        compactType={compactType}
        preventCollision={!compactType}
        isResizable={false}
        isDraggable
      >
        {children}
      </ResponsiveReactGridLayout>
    </div>
  );
}
